<template>
  <div class="checkout-view">
    <b-container>
      <section class="checkout-view__section">
        <purchase-steps active_id="data" />
      </section>

      <section class="checkout-view__section">
        <router-link
          :to="`/`"
          class="checkout-view__back"
        >
          <vb-icon :name="'angle_small_left'" :color="sass('info')" :size="18" />
          indietro
        </router-link>
        <h1 class="checkout-view__title">Completa il tuo acquisto</h1>
        <p class="checkout-view__text">
          Stai convertendo il <strong>tuo credito welfare</strong> in una <strong>gift card per viaggiare</strong>. <br />
          Verifica i tuoi dettagli e conferma l'ordine per <strong>riceverla subito via email</strong>.
        </p>
      </section>

      <section class="checkout-view__section">
        <b-row>
          <b-col xl="9">
            <b-row>
              <b-col lg="6">
                <selected-gift-card />
              </b-col>
              <b-col lg="6">
                <!-- h-100 pareggia il pannello all'altezza della card accanto -->
                <div class="checkout-view__panel h-100">
                  <after-purchase />
                </div>
              </b-col>
            </b-row>
            <trust-badges />
            <welfare-notice />
            <faq />
          </b-col>

          <b-col xl="3">
            <!--
              sidebar condivisa (globale, registrata in src/main.js): porta con sé
              i blocchi viaggiatore / pagamenti / consensi e il pulsante "Prenota".
            -->
            <sidebar
              :validate_on_mount="validate_on_mount"
              :integration="integration"
              :availability="availability"
              :live_price="live_price"
              :discount_codes="discount_codes"
              :purchasing="purchasing"
              :completed="completed"
              @purchase="(sidebar_data) => { book(sidebar_data) }"
            >
              <template #resume>
                <resume />
              </template>

              <!--slot messaggi di stato-->
              <template #message>
                <div v-if="!completed && purchasing" class="text-center">
                  <loader />
                </div>
              </template>
            </sidebar>
          </b-col>
        </b-row>
      </section>

      <!-- l'emissione passa da autorizzazione welfare ed email: non è istantanea -->
      <loader-modal :show="!completed && purchasing" service="book" />
    </b-container>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { service_handler } from '@services';
  import { build_order } from '@livegift/resources';
  import PurchaseSteps from '@livegift/components/blocks/purchase-steps.vue';
  import SelectedGiftCard from '@livegift/components/blocks/gift-card/selected-gift-card.vue';
  import AfterPurchase from '@livegift/components/blocks/checkout/after-purchase.vue';
  import TrustBadges from '@livegift/components/blocks/checkout/trust-badges.vue';
  import WelfareNotice from '@livegift/components/blocks/checkout/welfare-notice.vue';
  import Faq from '@livegift/components/blocks/checkout/faq.vue';
  import Resume from '@livegift/components/blocks/checkout/resume.vue';

  export default {
    name: 'checkout-view',

    components: {
      'purchase-steps': PurchaseSteps,
      'selected-gift-card': SelectedGiftCard,
      'after-purchase': AfterPurchase,
      'trust-badges': TrustBadges,
      'welfare-notice': WelfareNotice,
      'faq': Faq,
      'resume': Resume,
    },

    data() {
      return {
        integration: this.$config.integration || {},
        purchasing: false, // sidebar completata -> emissione in corso
        completed: false, // emissione conclusa, riuscita o no
        validate_on_mount: true,
      };
    },

    computed: {
      // la sidebar non dà un default alla prop `discount_codes` e ci fa .map()
      // senza guardia quando costruisce i pagamenti: va passata sempre
      ...mapState({ discount_codes: (state) => state.discount_codes }),
      ...mapState('livegift', ['selected_product', 'selected_amount']),

      // la sidebar tratta il prezzo come stringa (vedi logic/payments)
      live_price() {
        return String(this.selected_amount);
      },

      // credito welfare e codici sconto, derivati dalla config dal mixin globale
      availability() {
        return this.get_availability(this.live_price);
      },
    },

    created() {
      // al checkout si arriva solo dalla home: lo store non è persistito, quindi con un
      // accesso diretto o un refresh non ci sarebbe niente da acquistare. La guardia
      // scatta su qualsiasi falsy, così non si emette una card senza prodotto o da zero euro
      if (!this.selected_amount || !this.selected_product) this.$router.replace({ name: 'homepage' });
    },

    methods: {
      /**
       * Emissione della gift card (gc_checkout.php).
       *
       * Il payload che la sidebar emette combacia già con l'anagrafica che il
       * backend pretende, `payments` compreso: qui si aggiunge solo il carrello.
       * Una riga sola con `qty: 1`, perché il flusso vende una card per ordine;
       * `live_price` deve corrispondere alla somma dei nominali, altrimenti il
       * backend risponde `priceChanged`.
       *
       * @param {Object} sidebar_data payload dell'evento @purchase della sidebar
       */
      book(sidebar_data) {
        const guest = sidebar_data.guest.payload;

        const book = {
          guestFirstName: guest.guestFirstName,
          guestLastName: guest.guestLastName,
          guestEmail: guest.guestEmail,
          guestEmailConf: guest.guestEmailConf,
          guestPhone: guest.guestPhone,
          guestPrivacy: guest.guestPrivacy,
          bookingData: {
            live_price: this.live_price,
            cards: [{
              productCode: this.selected_product,
              valueNominal: this.selected_amount,
              qty: 1,
            }],
            payments: sidebar_data.payments,
          },
        };

        if (guest.beneficiaryId) book.beneficiaryId = guest.beneficiaryId;

        // il portale gift ha billToPax e bookerAddressRequired a false, quindi
        // questi due blocchi oggi non compaiono: li mappo comunque perché
        // `integration` ora è quella vera e un altro partner può accenderli
        if (sidebar_data.booker.payload !== false) {
          book.guestAddress = sidebar_data.booker.payload.booker_address;
          book.guestCity = sidebar_data.booker.payload.booker_city;
        }

        if (sidebar_data.bill_to_pax.payload !== false) {
          book.guestAddress = sidebar_data.bill_to_pax.payload.guestAddress;
          book.guestCity = sidebar_data.bill_to_pax.payload.guestCity;
          book.guestPostalCode = sidebar_data.bill_to_pax.payload.guestPostalCode;
          book.guestProvince = sidebar_data.bill_to_pax.payload.guestProvince;
          book.guestFiscalCode = sidebar_data.bill_to_pax.payload.guestFiscalCode;
        }

        this.purchasing = true;
        window.scrollTo(0, 0);

        service_handler('book', book, { track: 'checkout' }).then(
          (success) => {
            this.purchasing = false;
            this.completed = true;

            this.$store.commit('livegift/order', build_order(success.data));

            // la coppia in rotta rende la conferma condivisibile e ricaricabile:
            // al refresh lo store è vuoto e la pagina rilegge l'ordine da
            // gc_confirm. Va su `confirm` e non su `prenotazioni` perché è qui
            // che l'eventuale rimbalzo al portale welfare deve scattare
            this.$router.push({
              name: 'confirm',
              params: {
                confirm_code: success.data.confirmcode,
                gift_voucher_id: String(success.data.giftVoucherId),
              },
            });
          },
          (error) => {
            this.log('[book] - error');
            this.log(error.response);
            this.purchasing = false;
          },
        );
      },
    },
  };
</script>

<style lang="scss" scoped>

  .checkout-view {

    &__section {
      margin-top: $grid-gutter-width * 2;

      &:last-child {
        margin-bottom: $grid-gutter-width * 3;
      }
    }

    &__back {
      display: flex;
      align-items: center;
      margin-bottom: $grid-gutter-width / 2;

      .vb-icon {
        margin-top: 2px;
      }
    }

    // card bianca contenitore, ricorrente nella colonna di sinistra
    &__panel {
      @include livegift-panel;
    }

    &__title {
      margin: 0;
      font-size: 30px;
      font-weight: $vb-font-weight-semibold;
      color: $gray-900;
      line-height: normal;

      @include media-breakpoint-up(md) {
        font-size: $font-size-base * 2.25;
      }
    }

    &__text {
      margin: ($grid-gutter-width / 3) 0 0;
      font-size: $font-size-lg;
      color: $gray-700;
    }

    .selected-gift-card {
      margin-bottom: $grid-gutter-width;

      @include media-breakpoint-up(lg) {
        margin-bottom: 0;
      }
    }

    .faq {
      margin-bottom: $grid-gutter-width * 2;

      @include media-breakpoint-up(xl) {
        margin-bottom: 0;
      }
    }
  }
</style>